import styles from './Item.scss';

import cx from 'classnames';
import { Asset, ReferencedEntry } from 'contentful';
import { TFunction } from 'i18next';
import React from 'react';

import { useTranslation } from 'react-i18next';
import { ContentType, Product } from 'src/api/contentful/contentTypes.cloverdotcom';
import { ContentLink } from 'src/components/ContentLink/ContentLink';
import { ContentfulImage } from 'src/components/Contentful/ContentfulImage';
import { Text } from 'src/components/Contentful/Text';
import { MaterialIcon } from 'src/components/MaterialIcon/MaterialIcon';
import { ProductPrice } from 'src/components/ProductPrice/ProductPrice';
import { getContentType } from 'src/utils/CloverdotcomEntryUtil';
import { getField, isEntry } from 'src/utils/EntryUtil';
import { Environment } from 'src/utils/Environment';
import { shouldRedirectExistingMerchant } from 'src/utils/ExistingMerchant';

export interface IStickyItemProps extends React.AllHTMLAttributes<HTMLElement> {
  itemEntry?: ReferencedEntry<Product>;
  showPriceAsSubtitle?: boolean;
  useMinimalStyle?: boolean;
}

export type ItemProps = {
  itemEntry: ReferencedEntry<Product>;
  subtitle?: JSX.Element;
  children?: React.ReactNode;
  image?: Asset;
};

const ItemContent: React.FC<ItemProps> = ({ itemEntry, subtitle, image, children }) => (
  <>
    <div className={styles.stickyItemSection}>
      <ContentfulImage
        autoHeight
        asset={image}
        className={styles.stickyItemImage}
      />
      <div className={styles.stickyDescription}>
        <Text tagName={'h5'} entry={itemEntry} field="title" />
        {subtitle}
      </div>
    </div>

    <div className={styles.stickyItemSection}>
      {children}
    </div>
  </>
);

export const getStickyItemText = (t: TFunction, entry?: ReferencedEntry<Product>) => {
  if (!Environment.current.isExistingMerchant) return t('visit-shop');

  const link = getField(entry, 'link');

  switch (getContentType(link)) {
    case(ContentType.EXTERNAL_LINK):
      if (shouldRedirectExistingMerchant(link)) {
        return t('contact-sales');
      }
    default:
      return t('get-started');
  }
};

export const Item: React.FC<IStickyItemProps> = ({ itemEntry, showPriceAsSubtitle, useMinimalStyle }) => {
  const { t } = useTranslation();

  if (!isEntry(itemEntry)) return null;

  const image = getField(itemEntry, 'image');

  const priceDisplayText = <ProductPrice entry={itemEntry} />;
  const expandedPriceDisplayText = <ProductPrice entry={itemEntry} translationKey="starts-at-price" />;

  if (useMinimalStyle) {
    return (
      <ContentLink
          hideTitle
          entry={itemEntry}
          alwaysRenderChildren
          className={cx(styles.stickyItem, styles.stickyItemMinimal)}
      >
        <ItemContent
          itemEntry={itemEntry}
          subtitle={expandedPriceDisplayText}
        >
          <div className={styles.stickyItemLink}>
            <span className={styles.stickyItemLinkText}> {getStickyItemText(t, itemEntry)} </span>
            <MaterialIcon className={styles.stickyItemLinkArrow} icon="keyboard_arrow_right" />
          </div>
        </ItemContent>
      </ContentLink>
    );
  }

  return (
    <div className={styles.stickyItem}>
      <ItemContent
        itemEntry={itemEntry}
        subtitle={showPriceAsSubtitle ? expandedPriceDisplayText : undefined}
        image={image}
      >
        {showPriceAsSubtitle ? undefined : priceDisplayText}
        <ContentLink
          hideTitle
          entry={itemEntry}
          className={styles.stickyItemButton}
        >
          {getStickyItemText(t, itemEntry)}
        </ContentLink>
      </ItemContent>
    </div>
  );
};
